import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Modal, Button, Row, Col, Alert } from "react-bootstrap";

/** Services */
import {
  GetSchedulePaymentsReminders,
  UpdateScheduledPaymentReminders,
} from "../../../api/PaymentsReport";

/** Elements */
import ContentButton from "../../../components/global/ContentButton";
import TextLoading from "../../../components/global/TextLoading";
import FieldInput from "../../../components/global/form/FieldInput";
import LazyLoadingFormik from "../../../components/lazyLoading/LoadingFormik";

// Custom Styles
const customStyles = {
  description: {
    fontFamily: "Mulish",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "14px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    alignSelf: "stretch",
    marginTop: "10px",
  },
  secondayContainer: {
    width: "334px",
    height: "50px",
    borderRadius: "4px",
    padding: "8px",
    background: "#F6F6F6",
    border: "1px solid #D6DADB",
  },
  plusIcon: {
    cursor: "pointer",
    color: "#ea2c54",
    fontSize: "15px",
    marginLeft: "7px",
  },
  iconTrash: {
    marginLeft: "18px",
    marginTop: "16px",
    fontSize: "16px",
    cursor: "pointer",
    display: "inline-block",
  },
};

export default function ModalConfigPaymentReminder(props) {
  const [t] = useTranslation(["reminder", "global"]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reminders, setReminders] = useState({ days: [] });
  const [error, setError] = useState("");

  const validationSchema = Yup.object().shape({
    days: Yup.array().of(
      Yup.object().shape({
        value: Yup.string()
          .required(t("configurePaymentReminder.selectADay"))
          .test(
            "unique",
            t("configurePaymentReminder.selectADifferentDay"),
            function (value) {
              const days = this.from?.[1]?.value?.days;
              const values = days?.map((day) => day?.value);
              return values.indexOf(value) === values.lastIndexOf(value);
            }
          ),
      })
    ),
  });

  /* Initial Loading */
  useEffect(() => {
    getReminders();
  }, []);

  /**
   * Description: Function to get scheduled payment reminders
   */
  const getReminders = () => {
    setIsLoading(true);
    GetSchedulePaymentsReminders()
      .then((response) => {
        const defaultValue = { days: [{ value: "" }] };
        if (response?.status) {
          const reminders = response.data.map((item) => ({
            id: item.id,
            value: item.previous_days,
          }));
          setReminders(reminders.length ? { days: reminders } : defaultValue);
        } else {
          setReminders(defaultValue);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Description: Function to save the reminder configuration
   * @param {object} values
   */
  const saveReminderConfig = (values) => {
    const payload = {
      scheduled_payment_reminders: values.days.map((reminder) => {
        const day = {
          previous_days: reminder.value,
        };
        if (reminder.id) day.id = reminder.id;
        return day;
      }),
    };
    setIsSaving(true);
    UpdateScheduledPaymentReminders(payload)
      .then((response) => {
        if (response.status) {
          props.closeModal();
        } else {
          setError(response?.description);
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div>
      <Modal
        show={props.showConfigureReminder}
        aria-labelledby="contained-modal-title-lg"
      >
        <Modal.Header className="modal-header">
          <Modal.Title className="modal-title">
            {t("configurePaymentReminder.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-body mbottom10">
          <Row>
            <Col xs={12}>
              <p style={customStyles.description}>
                {t("configurePaymentReminder.reminderSendMasive")}
              </p>
            </Col>
            <Col xs={12} className="mt-2">
              <p style={customStyles.description}>
                {t("configurePaymentReminder.systemSendEmailAndPN")}
              </p>
            </Col>
          </Row>
          {!isLoading ? (
            <Formik
              initialValues={reminders}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={(values) => saveReminderConfig(values)}
            >
              {({ setFieldValue, values }) => (
                <Form style={customStyles.container}>
                  {values.days.map((reminder, index) => (
                    <Row key={index}>
                      {/* Days Select */}
                      <Col xs={8}>
                        <div style={customStyles.secondayContainer}>
                          <Row className="mb-3">
                            <Col xs={8}>
                              <FieldInput
                                name={"days[" + index + "].value"}
                                placeholder={t(
                                  "configurePaymentReminder.search"
                                )}
                                list={"days[" + index + "].valueList"}
                                required={true}
                                dataList={[
                                  {
                                    value: "0",
                                    label: t(
                                      "configurePaymentReminder.onDueDate"
                                    ),
                                  },
                                  { value: "1" },
                                  { value: "5" },
                                  { value: "10" },
                                ]}
                                onChange={(e) => {
                                  setFieldValue(
                                    "days[" + index + "].value",
                                    e.target.value
                                  );
                                }}
                                value={
                                  reminder.value === "0"
                                    ? t("configurePaymentReminder.onDueDate")
                                    : reminder.value
                                }
                              />
                            </Col>
                            {reminder.value !== "0" && (
                              <Col xs={4}>
                                <span>
                                  {t("configurePaymentReminder.daysBeforeDue")}
                                </span>
                              </Col>
                            )}
                          </Row>
                        </div>
                      </Col>
                      {/* Remove day Icon */}
                      <Col xs={4}>
                        <i
                          className="bi bi-trash"
                          style={{
                            ...customStyles.iconTrash,
                            ...(isSaving
                              ? {
                                  opacity: 0.5,
                                  pointerEvents: "unset",
                                  cursor: "not-allowed",
                                }
                              : {}),
                          }}
                          onClick={() => {
                            const newDays = values.days.filter(
                              (_, i) => i !== index
                            );
                            setFieldValue("days", newDays);
                          }}
                        />
                      </Col>
                    </Row>
                  ))}
                  {/* Add Reminder Button */}
                  <Row className="mb-2">
                    <Col xs={12}>
                      <span
                        style={values.days.length >= 5 ? { opacity: 0.5 } : {}}
                      >
                        {t("configurePaymentReminder.addReminder")}
                      </span>
                      <i
                        className="bi bi-plus-circle-fill"
                        style={{
                          ...customStyles.plusIcon,
                          ...(values.days.length >= 5 || isSaving
                            ? {
                                opacity: 0.5,
                                pointerEvents: "unset",
                                cursor: "not-allowed",
                              }
                            : {}),
                        }}
                        onClick={() => {
                          if (values.days.length < 5) {
                            setFieldValue("days", [
                              ...values.days,
                              {
                                value: "",
                              },
                            ]);
                          }
                        }}
                      />
                    </Col>
                  </Row>
                  {/**Errors */}
                  {error && (
                    <Row className="mt-3">
                      <Col md={12}>
                        <Alert variant="warning">{error}</Alert>
                      </Col>
                    </Row>
                  )}
                  {/* Modal Footer */}
                  <ContentButton className="content-button">
                    <Button
                      variant="outline-secondary"
                      onClick={() => props.closeModal()}
                      disabled={isSaving}
                    >
                      {t("global:buttons.cancel")}
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <TextLoading
                          text={t("global:buttons.saving")}
                          variant="light"
                        />
                      ) : (
                        t("global:buttons.save")
                      )}
                    </Button>
                  </ContentButton>
                </Form>
              )}
            </Formik>
          ) : (
            <LazyLoadingFormik />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

ModalConfigPaymentReminder.propTypes = {
  showConfigureReminder: PropTypes.bool,
  closeModal: PropTypes.func,
};
